import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  memo,
} from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { renderAsync } from "docx-preview";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

/**
 * AISearchPanel — Smart Candidate Discovery
 *
 * Modernized UI taking layout inspiration from the provided reference image,
 * but elevated with premium SaaS styling, soft shadows, and clean typography.
 */

/* ──────────────────────────────────────────────────────────────────────────
 * Theme tokens
 * ────────────────────────────────────────────────────────────────────────── */
const THEME = {
  matchHigh: "#22c55e", // Green (≥85)
  matchMid: "#f59e0b", // Orange/Amber (≥70)
  matchLow: "#ef4444",
};

/* ──────────────────────────────────────────────────────────────────────────
 * Modern Icons (Lucide-inspired)
 * ────────────────────────────────────────────────────────────────────────── */
const ModernIcons = {
  Pin: (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  ),
  Shield: (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6-8 10-8 10z" />
    </svg>
  ),
  Clock: (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  Briefcase: (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <rect width="20" height="14" x="2" y="7" rx="2" ry="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
  ),
  Sparkles: (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
      <path d="M20 3v4" />
      <path d="M22 5h-4" />
      <path d="M4 17v2" />
      <path d="M5 18H3" />
    </svg>
  ),
  Search: (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  ),
  FileText: (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
      <path d="M14 2v4a2 2 0 0 0 2 2h4" />
      <path d="M10 9H8" />
      <path d="M16 13H8" />
      <path d="M16 17H8" />
    </svg>
  ),
};

/* ──────────────────────────────────────────────────────────────────────────
 * Constants
 * ────────────────────────────────────────────────────────────────────────── */
const MAX_INITIAL_RESULTS = 10;
const RESUME_DOWNLOAD_BASE_URL = "/api/uploads/cvs/";
const LS_KEYS = { jdText: "rd_ai_search_jd_text", candidates: "rd_ai_search_top_matches" };

const SUMMARY_ALLOWED_TAGS = new Set(["B", "BR", "EM", "I", "LI", "OL", "P", "SPAN", "STRONG", "UL"]);
const SUMMARY_BLOCKED_TAGS = new Set(["EMBED", "IFRAME", "LINK", "META", "OBJECT", "SCRIPT", "STYLE"]);
const SUMMARY_ALLOWED_STYLES = new Set(["color", "font-style", "font-weight", "text-decoration"]);

/* ──────────────────────────────────────────────────────────────────────────
 * Pure utilities
 * ────────────────────────────────────────────────────────────────────────── */
const sanitizeInlineMatchSummary = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (typeof DOMParser === "undefined") {
    return raw.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "").replace(/\son\w+=("[^"]*"|'[^']*'|[^\s>]+)/gi, "");
  }
  const doc = new DOMParser().parseFromString(raw, "text/html");
  const sanitizeStyleValue = (styleValue) =>
    String(styleValue || "").split(";").map((d) => d.trim()).filter(Boolean).map((d) => {
        const [property, ...rest] = d.split(":");
        if (!property || rest.length === 0) return "";
        const name = property.trim().toLowerCase();
        const content = rest.join(":").trim();
        if (!SUMMARY_ALLOWED_STYLES.has(name) || /expression|javascript:|url\s*\(/i.test(content)) return "";
        return `${name}: ${content}`;
      }).filter(Boolean).join("; ");

  const unwrap = (el) => {
    const parent = el.parentNode;
    if (!parent) return el.remove();
    while (el.firstChild) parent.insertBefore(el.firstChild, el);
    parent.removeChild(el);
  };

  const walk = (node) => {
    Array.from(node.childNodes).forEach((child) => {
      if (child.nodeType === 3) return;
      if (child.nodeType !== 1) return child.parentNode?.removeChild(child);
      walk(child);
      const tag = child.tagName.toUpperCase();
      if (SUMMARY_BLOCKED_TAGS.has(tag)) return child.remove();
      if (!SUMMARY_ALLOWED_TAGS.has(tag)) return unwrap(child);
      Array.from(child.attributes).forEach((attr) => {
        if (attr.name.toLowerCase() === "style") {
          const safe = sanitizeStyleValue(attr.value);
          if (safe) child.setAttribute("style", safe);
          else child.removeAttribute(attr.name);
          return;
        }
        child.removeAttribute(attr.name);
      });
    });
  };
  walk(doc.body);
  return doc.body.innerHTML;
};

const buildResumeUrl = (fileName) => {
  const name = String(fileName || "").trim();
  if (!name) return "";
  if (/^https?:\/\//i.test(name)) return name;
  return `${RESUME_DOWNLOAD_BASE_URL}${encodeURIComponent(name)}`;
};

const getFileExtension = (fileNameOrUrl) => {
  const clean = String(fileNameOrUrl || "").split(/[?#]/)[0];
  const ext = clean.split(".").pop()?.toLowerCase();
  return ext && ext !== clean ? ext : "";
};

const coerceNumber = (value) => {
  if (typeof value === "string") {
    const n = Number(value.replace("%", "").trim());
    return Number.isFinite(n) ? n : null;
  }
  return Number.isFinite(Number(value)) ? Number(value) : null;
};

const clampPercent = (v) => (Number.isFinite(v) ? Math.max(0, Math.min(100, v)) : null);
const toPercent = (v) => (v === null ? null : v <= 10 ? v * 10 : v);
const pickText = (...values) => values.find((v) => typeof v === "string" && v.trim()) || "";
const toSkillList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  return String(value).split(",").map((s) => s.trim()).filter(Boolean);
};
const isPlaceholderText = (text) => {
  const t = String(text || "").trim().toLowerCase();
  return t === "location tbd" || t === "availability tbd" || t.includes("location tbd") || t.includes("availability tbd");
};

const normalizeSearchResults = (result) => {
  const list = Array.isArray(result?.ranking) ? result.ranking : Array.isArray(result?.data?.ranking) ? result.data.ranking : Array.isArray(result?.candidates) ? result.candidates : Array.isArray(result?.results) ? result.results : Array.isArray(result) ? result : [];
  if (list.length === 0) return [];

  const normalized = list.map((item, index) => {
    const ratingValue = coerceNumber(item.rating ?? item.match ?? item.score ?? item.fit_score);
    const matchPercent = clampPercent(toPercent(ratingValue)) ?? 70;
    const rankValue = coerceNumber(item.rank);
    const rawSkills = item.skills || item.primary_skills || item.matching_skills || item.matched_skills || "";
    const skills = toSkillList(rawSkills);
    const experience = item.experience_years || item.experience || item.total_experience;
    const resolvedSkills = [...skills, experience ? `${experience} Years` : null].filter(Boolean);
    const timezonePrimary = pickText(item.timezone_primary, item.timeZonePrimary, item.time_zone_primary, item.primary_timezone);
    const locationParts = [pickText(item.project_city, item.city), pickText(item.project_state, item.state), pickText(item.project_country, item.country)].filter(Boolean);
    const location = locationParts.length ? locationParts.join(", ") : pickText(item.location, item.locationDetail);
    const visa = pickText(item.authorization_name, item.work_authorization_name, item.workAuthorizationName, item.workAuth, item.visa);

    const generatedInfo = [
      location && { text: location, Icon: ModernIcons.Pin },
      timezonePrimary && { text: timezonePrimary, Icon: ModernIcons.Clock },
      visa && { text: visa, Icon: ModernIcons.Shield },
      experience && { text: `${experience} Years`, Icon: ModernIcons.Briefcase },
      item.availability && { text: item.availability, Icon: ModernIcons.Clock },
    ].filter(Boolean);
    const apiInfo = Array.isArray(item.info) ? item.info : [];
    const info = (apiInfo.length
      ? [
          location && { text: location, Icon: ModernIcons.Pin },
          timezonePrimary && { text: timezonePrimary, Icon: ModernIcons.Clock },
          ...apiInfo.filter((entry) => {
            const text = String(entry?.text || "").trim();
            return text && text !== location && text !== timezonePrimary;
          }),
        ].filter(Boolean)
      : generatedInfo
    ).filter((entry) => !isPlaceholderText(entry?.text));

    const ratingText = ratingValue === null ? "" : ratingValue <= 10 ? `${ratingValue} / 10` : `${clampPercent(toPercent(ratingValue))}%`;
    const candidateId = item.candidate_id || item.candidate_db_id || item.id || null;

    return {
      id: candidateId || `cand-${index}`,
      candidateId,
      name: pickText(item.candidate_name, item.name, item.full_name) || `Candidate ${index + 1}`,
      title: pickText(item.title, item.position, item.role) || "Candidate",
      match: matchPercent,
      rank: Number.isFinite(rankValue) ? rankValue : null,
      skills: resolvedSkills,
      info,
      location,
      timezonePrimary,
      visa,
      experienceYears: experience,
      summary: pickText(item.reason, item.summary, item.match_reason),
      email: pickText(item.masked_email, item.candidate_email, item.email, item.candidate_email_secondary),
      phone: pickText(item.masked_phone, item.candidate_phone, item.phone, item.candidate_phone_secondary),
      resume: pickText(item.file, item.resume, item.resume_file),
      ratingText,
    };
  });

  const hasRank = normalized.some((item) => item.rank !== null);
  if (!hasRank) return normalized;
  return [...normalized].sort((a, b) => {
    if (a.rank === null && b.rank === null) return 0;
    if (a.rank === null) return 1;
    if (b.rank === null) return -1;
    return a.rank - b.rank;
  });
};

const mergeCandidateWithDetail = (candidate, detail) => {
  const info = detail?.candidateInfo || {};
  const timezonePrimary = pickText(
    info.timeZonePrimary,
    info.timezone_primary,
    detail?.timezonePrimary,
    detail?.timezone_primary,
    candidate.timezonePrimary
  );
  const detailLocationParts = [info.city, info.state, info.country].filter(Boolean).join(", ");
  const locationText = detailLocationParts || detail?.location || detail?.locationDetail || "";
  const availabilityText = detail?.availability || detail?.availabilityStatus || info?.candidateResponse || "";
  const primarySkills = detail?.skillGroups?.primary || toSkillList(info.primarySkills);
  const secondarySkills = detail?.skillGroups?.secondary || toSkillList(info.secondarySkills);
  const mergedSkills = [...primarySkills, ...secondarySkills].filter(Boolean);

  return {
    ...candidate,
    name: detail?.name || candidate.name,
    title: detail?.title || info?.title || candidate.title,
    email: info?.emailId || info?.secondaryEmailId || candidate.email,
    phone: info?.phoneNumber || info?.secondaryPhoneNumber || candidate.phone,
    currentRate: detail?.contractDetails?.rate || candidate.currentRate || "N/A",
    expectedSalary: detail?.contractDetails?.expectedSalary || info?.expectedSalary || candidate.expectedSalary || "N/A",
    employmentType: detail?.contractDetails?.employmentType || candidate.employmentType || "N/A",
    availabilityLabel: availabilityText,
    location: locationText || candidate.location,
    timezonePrimary,
    info: [
      locationText && { text: locationText, Icon: ModernIcons.Pin },
      timezonePrimary && { text: timezonePrimary, Icon: ModernIcons.Clock },
      availabilityText && { text: availabilityText, Icon: ModernIcons.Clock },
    ].filter(Boolean),
    skills: mergedSkills.length ? mergedSkills : candidate.skills,
    summary: candidate.summary || detail?.headline || "",
  };
};

/* ──────────────────────────────────────────────────────────────────────────
 * Animated match score ring — styled closely to the reference image
 * ────────────────────────────────────────────────────────────────────────── */
const MatchScoreRing = memo(({ percentage, delay = 0 }) => {
  const [display, setDisplay] = useState(0);
  const prefersReduced = useReducedMotion();

  useEffect(() => {
    if (prefersReduced) {
      setDisplay(percentage);
      return;
    }
    let frame, start;
    const duration = 1200;
    const initial = 0;
    const tick = (t) => {
      if (start === undefined) start = t;
      const progress = Math.min((t - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(initial + (percentage - initial) * eased));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    const startTimer = setTimeout(() => { frame = requestAnimationFrame(tick); }, delay * 1000);
    return () => { clearTimeout(startTimer); if (frame) cancelAnimationFrame(frame); };
  }, [percentage, delay, prefersReduced]);

  const color = percentage >= 85 ? THEME.matchHigh : percentage >= 70 ? THEME.matchMid : THEME.matchLow;
  const ring = `conic-gradient(${color} ${percentage * 3.6}deg, #f1f5f9 0deg)`;

  return (
    <div 
      className="relative flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full" 
      style={{ background: ring }} 
      aria-label={`Match score ${percentage}%`}
    >
      <div className="flex h-[56px] w-[56px] flex-col items-center justify-center rounded-full bg-white leading-none shadow-[inset_0_1px_4px_rgba(0,0,0,0.05)]">
        <span className="text-[17px] font-black tracking-tight" style={{ color: color }}>
          {display}<span className="text-[11px]">%</span>
        </span>
        <span className="mt-0.5 text-[9px] font-bold text-slate-400">
          match
        </span>
      </div>
    </div>
  );
});
MatchScoreRing.displayName = "MatchScoreRing";

/* ──────────────────────────────────────────────────────────────────────────
 * Document Preview
 * ────────────────────────────────────────────────────────────────────────── */
const PreviewNotice = ({ title, children }) => (
  <div className="flex h-full items-center justify-center bg-slate-50 p-6 text-center">
    <div className="max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <ModernIcons.FileText className="mx-auto mb-3 h-8 w-8 text-slate-400" />
      <p className="text-sm font-bold text-slate-900">{title}</p>
      <p className="mt-2 text-sm text-slate-500">{children}</p>
    </div>
  </div>
);

const DocxPreview = ({ fileUrl }) => {
  const containerRef = useRef(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const container = containerRef.current;
    if (!container || !fileUrl) return undefined;
    container.innerHTML = "";
    setError("");
    setIsLoading(true);

    (async () => {
      try {
        const res = await fetch(fileUrl);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (blob.type?.includes("html")) throw new Error("The server returned a page instead of the DOCX file.");
        await renderAsync(blob, container, undefined, { breakPages: true, inWrapper: true, renderFooters: true, renderHeaders: true });
        if (!cancelled) setIsLoading(false);
      } catch (err) {
        if (!cancelled) { setError(err?.message || "Unable to preview this DOCX file."); setIsLoading(false); }
      }
    })();

    return () => { cancelled = true; container.innerHTML = ""; };
  }, [fileUrl]);

  if (error) return <PreviewNotice title="Preview failed">{error}</PreviewNotice>;
  return (
    <div className="relative h-full overflow-auto bg-slate-100/50 p-5">
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80 backdrop-blur-sm">
          <span className="flex items-center gap-2 text-sm font-semibold text-slate-600">
             <ModernIcons.Sparkles className="h-4 w-4 animate-spin text-violet-500" /> Loading Document...
          </span>
        </div>
      )}
      <div ref={containerRef} className="mx-auto min-h-full w-fit max-w-full rounded-lg shadow-sm" />
    </div>
  );
};

const DocumentPreview = ({ fileName, fileUrl }) => {
  const ext = getFileExtension(fileName || fileUrl);
  if (ext === "docx") return <DocxPreview fileUrl={fileUrl} />;
  if (ext === "pdf") return <iframe title={fileName || "Resume preview"} src={fileUrl} className="h-full w-full border-0 bg-white" />;
  if (ext === "doc") return <PreviewNotice title="Convert DOC first">Legacy DOC files must be converted to DOCX or PDF before preview.</PreviewNotice>;
  return <PreviewNotice title="Preview not available">This file type is not supported for preview.</PreviewNotice>;
};

const ResumeModal = ({ candidate, resumeUrl, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <motion.div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-md" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
      <motion.div className="flex h-[88vh] w-full max-w-5xl flex-col overflow-hidden rounded-[24px] border border-white/20 bg-white shadow-[0_32px_64px_-12px_rgba(0,0,0,0.3)] ring-1 ring-slate-200" initial={{ opacity: 0, scale: 0.96, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96, y: 20 }} transition={{ type: "spring", stiffness: 300, damping: 30 }} onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center justify-between border-b border-slate-100 bg-slate-50/80 px-6 py-4 backdrop-blur-md">
          <div className="flex items-center gap-3 min-w-0">
             <div className="flex h-10 w-10 items-center justify-center rounded-full bg-violet-100 text-violet-600">
                <ModernIcons.FileText className="h-5 w-5" />
             </div>
            <div>
              <h4 className="truncate text-sm font-bold text-slate-900">{candidate.resume || `${candidate.name} Resume`}</h4>
              <p className="text-xs font-medium text-slate-500">Document Preview</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="rounded-full bg-slate-200/50 px-4 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-200 hover:text-slate-900">Close</button>
        </header>
        <div className="min-h-0 flex-1 bg-slate-50/50">
          <DocumentPreview fileName={candidate.resume} fileUrl={resumeUrl} />
        </div>
      </motion.div>
    </motion.div>
  );
};

/* ──────────────────────────────────────────────────────────────────────────
 * Candidate Card — Styled to reference layout with premium touches
 * ────────────────────────────────────────────────────────────────────────── */
const CandidateCard = memo(({ candidate, index, onOpenCandidate }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isResumeOpen, setIsResumeOpen] = useState(false);

  const isTopMatch = index === 0 || candidate.rank === 1;
  const resumeUrl = useMemo(() => buildResumeUrl(candidate.resume), [candidate.resume]);
  const summaryHtml = useMemo(() => sanitizeInlineMatchSummary(candidate.summary), [candidate.summary]);
  const metaItems = useMemo(() => {
    const fromInfo = Array.isArray(candidate.info) ? candidate.info : [];
    const fallback = [
      candidate.location && { text: candidate.location, Icon: ModernIcons.Pin },
      candidate.timezonePrimary && { text: candidate.timezonePrimary, Icon: ModernIcons.Clock },
      candidate.visa && { text: candidate.visa, Icon: ModernIcons.Shield },
      candidate.experienceYears && {
        text: `${candidate.experienceYears} Years`,
        Icon: ModernIcons.Clock,
      },
    ].filter(Boolean);
    return (fromInfo.length ? fromInfo : fallback)
      .filter((item) => item?.text)
      .slice(0, 4);
  }, [candidate.experienceYears, candidate.info, candidate.location, candidate.timezonePrimary, candidate.visa]);

  const fallbackEmail = candidate.name ? `${candidate.name.toLowerCase().replace(/\s+/g, ".")}@example.com` : "unknown@example.com";
  const email = candidate.email || fallbackEmail;
  const phone = candidate.phone || "N/A";

  const canOpenDetail = Boolean(candidate.candidateId);

  const handleViewMore = useCallback(async () => {
    if (canOpenDetail && typeof onOpenCandidate === "function") {
      await onOpenCandidate(candidate);
      setIsExpanded(true);
      return;
    }
    setIsExpanded((v) => !v);
  }, [canOpenDetail, onOpenCandidate, candidate]);

  return (
    <motion.article
      className={`group relative overflow-hidden rounded-[20px] bg-white p-5 transition-all duration-300 ${
        isTopMatch 
          ? "border-[1.5px] border-amber-400/40 shadow-[0_4px_24px_-10px_rgba(245,158,11,0.25)]" 
          : "border border-slate-200 shadow-sm hover:border-slate-300 hover:shadow-md"
      }`}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.04, 0.4), duration: 0.4 }}
    >
      {/* Top Header Row: Score + Info + Button */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-4">
           {/* Score Ring */}
           <MatchScoreRing percentage={candidate.match} delay={Math.min(index * 0.04, 0.4)} />
           
           {/* Name & Title */}
           <div className="min-w-0 flex-1">
             <div className="flex items-center flex-wrap gap-2 mb-0.5">
                <h4 className="text-lg font-bold text-slate-900 tracking-tight leading-none">
                  {candidate.name}
                </h4>
                {candidate.rank !== null && candidate.rank !== undefined && (
                  <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-bold text-violet-700 leading-none">
                    Rank #{candidate.rank}
                  </span>
                )}
             </div>
             {metaItems.length > 0 && (
               <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5">
                 {metaItems.map((item, itemIndex) => {
                   const ItemIcon = item.Icon;
                   return (
                     <span
                       key={`${item.text}-${itemIndex}`}
                       className="inline-flex items-center gap-1.5 text-[12px] font-medium text-slate-500"
                     >
                       {ItemIcon && (
                         <ItemIcon className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
                       )}
                       {item.text}
                     </span>
                   );
                 })}
               </div>
             )}
           </div>
        </div>

        {/* Action Button */}
        <div className="flex-shrink-0 flex items-center gap-2">
          {resumeUrl && (
            <button 
              type="button" 
              className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50" 
              onClick={(e) => { e.stopPropagation(); setIsResumeOpen(true); }}
            >
              <ModernIcons.FileText className="h-3.5 w-3.5" /> 
              Resume
            </button>
          )}
          <button 
            type="button" 
            onClick={handleViewMore} 
            className="rounded-full bg-violet-600 px-5 py-2 text-xs font-bold text-white transition hover:bg-violet-700 shadow-sm"
          >
            {canOpenDetail ? "View More" : isExpanded ? "Show Less" : "View More"}
          </button>
        </div>
      </div>

      {/* Why Match Section */}
      {summaryHtml && (
        <div className="mt-4 pl-[72px]">
          <p className="mb-1 text-[13px] font-medium text-slate-500">Why match:</p>
          <div 
            className="text-[13px] leading-[1.6] text-slate-700 [&_b]:font-semibold [&_strong]:font-semibold"
            // The dangerouslySetInnerHTML relies on the backend injecting colors (e.g., <span style="color:green">MATCHED:</span>)
            // It will render smoothly within this wrapper.
            dangerouslySetInnerHTML={{ __html: summaryHtml }} 
          />
        </div>
      )}

      {/* Expanded Details */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }} 
            animate={{ opacity: 1, height: "auto" }} 
            exit={{ opacity: 0, height: 0 }} 
            transition={{ duration: 0.25 }} 
            className="mt-5 pl-[72px] overflow-hidden"
          >
            <div className="border-t border-slate-100 pt-4">
              <dl className="grid grid-cols-1 gap-x-6 gap-y-4 text-[13px] sm:grid-cols-2">
                <DetailRow label="Location" value={candidate.location} />
                <DetailRow label="Visa" value={candidate.visa} />
                <DetailRow label="Experience" value={candidate.experienceYears ? `${candidate.experienceYears} Years` : null} />
                <DetailRow label="Email" value={email} mono />
                <DetailRow label="Phone" value={phone} mono />
                <DetailRow label="Availability" value={candidate.availabilityLabel || "Available"} valueClass="text-emerald-600 font-bold" />
                <DetailRow label="Rate" value={candidate.currentRate} />
                <DetailRow label="Expected Salary" value={candidate.expectedSalary} />
                <DetailRow label="Employment Type" value={candidate.employmentType} />
                {candidate.resume && (
                  <div className="flex flex-col gap-1">
                    <dt className="font-bold text-slate-400 uppercase tracking-wider text-[10px]">Resume</dt>
                    <dd>
                      {resumeUrl ? (
                        <button type="button" className="font-bold text-violet-600 hover:text-violet-700 hover:underline" onClick={() => setIsResumeOpen(true)}>{candidate.resume}</button>
                      ) : (
                        <span className="font-medium text-slate-800">{candidate.resume}</span>
                      )}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isResumeOpen && <ResumeModal candidate={candidate} resumeUrl={resumeUrl} onClose={() => setIsResumeOpen(false)} />}
      </AnimatePresence>
    </motion.article>
  );
});
CandidateCard.displayName = "CandidateCard";

const DetailRow = ({ label, value, valueClass = "text-slate-800 font-medium", mono = false }) => (
  <div className="flex flex-col gap-1">
    <dt className="font-bold text-slate-400 uppercase tracking-wider text-[10px]">{label}</dt>
    <dd className={`${valueClass} ${mono ? "font-mono text-[11px]" : "text-[13px]"}`}>{value || "N/A"}</dd>
  </div>
);

/* ──────────────────────────────────────────────────────────────────────────
 * Skeleton
 * ────────────────────────────────────────────────────────────────────────── */
const CardSkeleton = () => (
  <div className="rounded-[20px] border border-slate-200 bg-white p-5">
    <div className="flex items-start gap-4">
      <Skeleton circle width={64} height={64} />
      <div className="flex-1 space-y-4">
        <div className="flex items-center justify-between">
          <div className="w-1/2 space-y-2">
            <Skeleton height={20} width="60%" borderRadius={4} />
            <Skeleton height={14} width="40%" borderRadius={4} />
          </div>
          <Skeleton height={36} width={100} borderRadius={20} />
        </div>
        <div className="space-y-2 pl-2">
          <Skeleton height={14} width="15%" />
          <Skeleton height={14} width="80%" />
          <Skeleton height={14} width="60%" />
        </div>
      </div>
    </div>
  </div>
);

/* ──────────────────────────────────────────────────────────────────────────
 * Search Panel (Left Column) — Modeled precisely after the reference layout
 * ────────────────────────────────────────────────────────────────────────── */
const SearchPanel = ({ jdText, onJdTextChange, onSearch, isSearching, searchError }) => {
  const textareaRef = useRef(null);
  const trimmedLength = jdText.trim().length;

  return (
    <motion.div
      className="relative flex min-h-[500px] flex-col overflow-hidden rounded-[24px] bg-white p-6 shadow-[0_4px_24px_rgba(0,0,0,0.04)] lg:h-[calc(100vh-6rem)] lg:min-h-[580px]"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      <div className="relative flex h-full flex-1 flex-col gap-6">
        
        {/* Top Header Row matching the image's layout structure */}
        <div className="flex flex-col gap-4">
          <div className="flex gap-4 items-start">
            {/* AI Search Badge Pill */}
            <div className="flex items-center gap-1.5 rounded-[20px] bg-gradient-to-r from-violet-600 to-violet-400 px-4 py-3 shadow-[0_8px_16px_-6px_rgba(124,58,237,0.4)]">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-white/20">
                <ModernIcons.Sparkles className="h-3.5 w-3.5 text-white" />
              </div>
              <span className="text-[17px] font-black uppercase tracking-wide text-white leading-tight">
                AI<br />Search
              </span>
            </div>

            {/* Main Title */}
            <h2 className="text-[32px] font-black leading-[1] tracking-tight text-[#0f172a] pt-1">
              Smart<br />Candidate<br />Discovery
            </h2>
          </div>

          {/* Indicator Tags */}
          <div className="grid grid-cols-3 gap-2">
            <span className="rounded-xl bg-[#eff6ff] px-3 py-2 text-center text-[11px] font-bold leading-tight text-[#1d4ed8]">
              Intent Parsing
            </span>
            <span className="rounded-xl bg-[#ecfdf5] px-3 py-2 text-center text-[11px] font-bold leading-tight text-[#047857]">
              Skill Match
            </span>
            <span className="rounded-xl bg-[#f5f3ff] px-3 py-2 text-center text-[11px] font-bold leading-tight text-[#6d28d9]">
              Fit Score
            </span>
          </div>
        </div>

        {/* Text Area Section */}
        <div className="flex flex-1 flex-col mt-4">
          <div className="mb-2 flex items-center justify-between px-1">
            <label className="text-[11px] font-black uppercase tracking-[0.2em] text-[#475569]">
              Job Description
            </label>
            <span className="text-[11px] font-bold text-[#94a3b8]">
              {trimmedLength} chars
            </span>
          </div>

          <div className="flex-1 rounded-[16px] border border-[#e2e8f0] bg-white transition-all focus-within:border-violet-400 focus-within:ring-2 focus-within:ring-violet-100 hover:border-slate-300">
            <textarea
              placeholder="Paste Job Description, core skills, required experience, or specific roles..."
              className="h-full w-full resize-none rounded-[16px] bg-transparent p-5 text-[14px] leading-relaxed text-slate-700 outline-none placeholder:text-slate-400"
              value={jdText}
              onChange={(e) => onJdTextChange(e.target.value)}
              ref={textareaRef}
            />
          </div>
        </div>

        {searchError && (
          <div className="rounded-[12px] bg-rose-50 px-4 py-3 text-[13px] font-bold text-rose-600 ring-1 ring-inset ring-rose-500/20">
            {searchError}
          </div>
        )}

        {/* Extract & Search Button (Vibrant Pill style at the bottom) */}
        <motion.button
          className="mt-2 flex w-full items-center justify-between rounded-[20px] bg-gradient-to-r from-violet-600 to-fuchsia-500 p-[18px] text-white shadow-[0_8px_20px_rgba(192,38,211,0.25)] transition-transform hover:scale-[1.01] active:scale-95 disabled:opacity-70 disabled:hover:scale-100"
          onClick={onSearch}
          disabled={isSearching}
        >
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/20">
              {isSearching ? (
                 <ModernIcons.Sparkles className="h-4 w-4 animate-spin" />
              ) : (
                 <ModernIcons.Search className="h-4 w-4" />
              )}
            </div>
            <span className="text-left text-[16px] font-bold leading-[1.2] tracking-wide">
              {isSearching ? "Searching..." : "Extract &\nSearch"}
            </span>
          </div>
          <span className="text-right text-[13px] font-medium text-white/90 leading-tight">
            Results synced from<br />backend.
          </span>
        </motion.button>
      </div>
    </motion.div>
  );
};

/* ──────────────────────────────────────────────────────────────────────────
 * Results Panel (Right Column)
 * ────────────────────────────────────────────────────────────────────────── */
const ResultsPanel = ({ candidates, isLoading, hasSearched, showAll, onToggleShowAll, onOpenCandidate }) => {
  const showInitialSkeleton = isLoading && candidates.length === 0;
  const showRefreshOverlay = isLoading && candidates.length > 0;
  const visible = showAll ? candidates : candidates.slice(0, MAX_INITIAL_RESULTS);
  const hasMore = candidates.length > MAX_INITIAL_RESULTS;

  if (!isLoading && candidates.length === 0) {
    return (
      <motion.div className="flex min-h-[460px] flex-col items-center justify-center rounded-[24px] border border-dashed border-slate-300 bg-white/60 p-10 text-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <div className="mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-slate-100 text-slate-400">
          <ModernIcons.Search className="h-8 w-8" />
        </div>
        <h3 className="text-xl font-bold tracking-tight text-slate-900">{hasSearched ? "No Candidates Found" : "Awaiting Input"}</h3>
        <p className="mt-2 max-w-sm text-[14px] text-slate-500">
          {hasSearched ? "No resumes matched this criteria. Try simplifying the prompt." : "Paste a job description to instantly surface the best candidates based on deep semantic matching."}
        </p>
      </motion.div>
    );
  }

  return (
    <motion.section className="flex flex-col gap-4" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
      <header className="flex items-center justify-between gap-4 pb-2 px-1">
        <div className="flex items-center gap-3">
          <h3 className="text-[20px] font-black tracking-tight text-slate-900">Top Matches</h3>
          <span className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-bold text-slate-600">
            {isLoading ? "Scanning..." : showAll ? `${candidates.length} Results` : `Top ${Math.min(MAX_INITIAL_RESULTS, candidates.length)} of ${candidates.length}`}
          </span>
        </div>
        {isLoading && (
          <span className="inline-flex items-center gap-2 text-[13px] font-bold text-violet-600">
            <ModernIcons.Sparkles className="h-3.5 w-3.5 animate-spin" /> Updating
          </span>
        )}
      </header>

      <div className="relative flex flex-col gap-5">
        {showInitialSkeleton ? Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} />) : visible.map((candidate, index) => (
          <CandidateCard key={candidate.id} candidate={candidate} index={index} onOpenCandidate={onOpenCandidate} />
        ))}
        {showRefreshOverlay && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-[24px] bg-white/40 backdrop-blur-[2px]">
            <span className="flex items-center gap-2 rounded-full bg-slate-900 px-6 py-3 text-[14px] font-bold text-white shadow-xl">
              <ModernIcons.Sparkles className="h-4 w-4 animate-spin text-violet-400" /> Re-ranking matches...
            </span>
          </div>
        )}
      </div>

      {hasMore && !isLoading && (
        <div className="mt-2 flex justify-center pb-8">
          <button type="button" onClick={onToggleShowAll} className="rounded-full border border-slate-200 bg-white px-8 py-2.5 text-[14px] font-bold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50">
            {showAll ? "Show Top 10 Only" : `View All ${candidates.length} Matches`}
          </button>
        </div>
      )}
    </motion.section>
  );
};

/* ──────────────────────────────────────────────────────────────────────────
 * Main Panel
 * ────────────────────────────────────────────────────────────────────────── */
const AISearchPanel = ({ rdAiSearchEndpoint, onFetchCandidateById, onOpenCandidate }) => {
  const [candidates, setCandidates] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [jdText, setJdText] = useState("");
  const [searchError, setSearchError] = useState("");

  useEffect(() => {
    try {
      const savedJd = localStorage.getItem(LS_KEYS.jdText);
      if (savedJd) setJdText(savedJd);
      const savedCands = localStorage.getItem(LS_KEYS.candidates);
      if (savedCands) {
        const parsed = JSON.parse(savedCands);
        if (Array.isArray(parsed) && parsed.length) { setCandidates(parsed); setHasSearched(true); }
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const id = setTimeout(() => { try { localStorage.setItem(LS_KEYS.jdText, jdText || ""); } catch { /* ignore */ } }, 400);
    return () => clearTimeout(id);
  }, [jdText]);

  useEffect(() => {
    try { localStorage.setItem(LS_KEYS.candidates, JSON.stringify(candidates || [])); } catch { /* ignore */ }
  }, [candidates]);

  const handleSearch = useCallback(async () => {
    const trimmed = jdText.trim();
    if (!trimmed) { setSearchError("Please paste a JD to initiate a search."); return; }
    if (!rdAiSearchEndpoint) { setSearchError("Search endpoint configuration is missing."); return; }
    setSearchError(""); setIsSearching(true);
    try {
      const body = new URLSearchParams(); body.append("search", trimmed);
      const response = await fetch(rdAiSearchEndpoint, { method: "POST", headers: { "Content-Type": "application/x-www-form-urlencoded" }, body: body.toString() });
      const result = await response.json();
      const apiMessage = (typeof result?.error === "string" && result.error.trim()) || (typeof result?.message === "string" && result.message.trim()) || "";
      const isNoResume = /no valid resumes found|no matched resume|no resumes found|error processing resumes/i.test(apiMessage);
      if (!response.ok || result?.status === false) {
        if (isNoResume) { setCandidates([]); setHasSearched(true); setSearchError("No resumes found matching this description."); return; }
        throw new Error(apiMessage || "Search request encountered an error.");
      }
      const normalized = normalizeSearchResults(result);
      setCandidates(normalized); setHasSearched(true); setShowAll(false);
    } catch (err) {
      setSearchError(err.message || "Search failed. Please verify your connection and try again.");
    } finally { setIsSearching(false); }
  }, [jdText, rdAiSearchEndpoint]);

  const handleOpenCandidate = useCallback(async (candidate) => {
    if (typeof onOpenCandidate === "function") { await onOpenCandidate(candidate); return; }
    const id = candidate?.candidateId;
    if (!id || typeof onFetchCandidateById !== "function") return;
    try {
      const detail = await onFetchCandidateById(id);
      if (!detail) return;
      setCandidates((prev) => prev.map((item) => String(item.candidateId) === String(id) ? mergeCandidateWithDetail(item, detail) : item));
    } catch { /* keep existing card data */ }
  }, [onFetchCandidateById, onOpenCandidate]);

  return (
    <div className="min-h-screen bg-[#f8f9fa] font-sans selection:bg-violet-200 selection:text-violet-900">
      <div className="mx-auto max-w-[1536px] px-4 py-8 sm:px-6 lg:py-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(400px,440px)_minmax(0,1fr)] lg:items-start xl:gap-10">
          {/* Left Column (Search Tool) */}
          <div className="lg:sticky lg:top-6 z-10">
            <SearchPanel jdText={jdText} onJdTextChange={setJdText} onSearch={handleSearch} isSearching={isSearching} searchError={searchError} />
          </div>
          {/* Right Column (Results) */}
          <div className="min-w-0 pb-20">
            <ResultsPanel candidates={candidates} isLoading={isSearching} hasSearched={hasSearched} showAll={showAll} onToggleShowAll={() => setShowAll((v) => !v)} onOpenCandidate={handleOpenCandidate} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AISearchPanel;
